import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Play, 
  Pause, 
  RotateCcw, 
  X,
  Package,
  Calculator,
  Heart,
  Globe,
  MapPin,
  Clock,
  CheckCircle,
  DollarSign
} from 'lucide-react';

interface DemoVideoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const demoScenes = [
  {
    id: 'intro',
    duration: 3000,
    title: 'Welcome to TravelPal',
    content: (
      <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
          <Package className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold">TravelPal Demo</h2>
        <p className="text-muted-foreground">See how easy travel planning can be</p>
      </div>
    )
  },
  {
    id: 'packing',
    duration: 4000,
    title: 'Smart Packing Assistant',
    content: (
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-6 h-6 text-primary" />
          <h3 className="text-lg font-semibold">Packing for Paris</h3>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span>Destination:</span>
            <span className="font-medium">Paris, France</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Duration:</span>
            <span className="font-medium">7 days</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Weather:</span>
            <span className="font-medium">Spring (15-20°C)</span>
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-medium text-sm">Recommended Items:</p>
          {['Rain jacket', 'Comfortable walking shoes', 'Light sweater', 'Umbrella', 'Passport'].map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-sm animate-fade-in" style={{ animationDelay: `${index * 300}ms` }}>
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between text-sm bg-primary/10 rounded p-2">
          <span>Estimated packing time:</span>
          <span className="font-bold text-primary">45 minutes</span>
        </div>
      </div>
    )
  },
  {
    id: 'currency',
    duration: 4000,
    title: 'Currency Converter',
    content: (
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="w-6 h-6 text-primary" />
          <h3 className="text-lg font-semibold">Live Exchange Rates</h3>
        </div>
        
        <div className="text-center space-y-2">
          <div className="text-4xl font-bold">$500</div>
          <div className="text-2xl text-muted-foreground">↓</div>
          <div className="text-4xl font-bold text-primary">€460.50</div>
          <div className="text-sm text-muted-foreground">1 USD = 0.9210 EUR</div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">Receipt Scanner</span>
            <Badge variant="secondary" className="animate-pulse">3 receipts scanned</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Total Spent</span>
            <span className="font-bold">€127.80</span>
          </div>
        </div>

        <div className="text-center">
          <Button size="sm" className="w-full">
            <DollarSign className="w-4 h-4 mr-2" />
            Scan New Receipt
          </Button>
        </div>
      </div>
    )
  },
  {
    id: 'health',
    duration: 4000,
    title: 'Health & Wellness',
    content: (
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-6 h-6 text-primary" />
          <h3 className="text-lg font-semibold">Health Guide</h3>
        </div>
        
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">No vaccinations required</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Routine vaccinations up to date</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-medium text-sm">Health Tips:</p>
          {['Stay hydrated during walks', 'Comfortable shoes recommended', 'Pack basic first aid'].map((tip, index) => (
            <div key={index} className="flex items-center gap-2 text-sm animate-fade-in" style={{ animationDelay: `${index * 400}ms` }}>
              <div className="w-2 h-2 bg-primary rounded-full" />
              <span>{tip}</span>
            </div>
          ))}
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-sm">
            <span className="font-medium text-red-800">Emergency:</span>
            <span className="ml-2 text-red-700">+33 15 (SAMU)</span>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'language',
    duration: 4000,
    title: 'Language Helper',
    content: (
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-6 h-6 text-primary" />
          <h3 className="text-lg font-semibold">French Essentials</h3>
        </div>
        
        <div className="space-y-2">
          <p className="font-medium text-sm">Essential Phrases:</p>
          {[
            'Bonjour - Hello',
            'Merci - Thank you', 
            'Où est... - Where is...',
            'Combien? - How much?'
          ].map((phrase, index) => (
            <div key={index} className="bg-muted/50 rounded p-2 text-sm animate-fade-in" style={{ animationDelay: `${index * 300}ms` }}>
              {phrase}
            </div>
          ))}
        </div>

        <div className="bg-primary/10 rounded-lg p-3">
          <p className="font-medium text-sm mb-2">Live Translation:</p>
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground">English:</div>
            <div className="text-sm">"Do you speak English?"</div>
            <div className="text-sm text-muted-foreground">French:</div>
            <div className="text-sm font-medium">"Parlez-vous anglais?"</div>
          </div>
        </div>

        <Button size="sm" className="w-full">
          <Globe className="w-4 h-4 mr-2" />
          Try Voice Translation
        </Button>
      </div>
    )
  },
  {
    id: 'outro',
    duration: 3000,
    title: 'Ready to Start?',
    content: (
      <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
        <div className="flex gap-2">
          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
            <Package className="w-6 h-6 text-primary" />
          </div>
          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
            <Calculator className="w-6 h-6 text-primary" />
          </div>
          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
            <Heart className="w-6 h-6 text-primary" />
          </div>
          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
            <Globe className="w-6 h-6 text-primary" />
          </div>
        </div>
        <h2 className="text-2xl font-bold">All your travel tools in one place</h2>
        <p className="text-muted-foreground">Start planning your next adventure</p>
        <Button size="lg" className="bg-primary text-primary-foreground">
          Get Started Now
        </Button>
      </div>
    )
  }
];

export function DemoVideo({ open, onOpenChange }: DemoVideoProps) {
  const [currentScene, setCurrentScene] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [sceneProgress, setSceneProgress] = useState(0);

  const totalDuration = demoScenes.reduce((acc, scene) => acc + scene.duration, 0);
  const currentSceneData = demoScenes[currentScene];

  useEffect(() => {
    if (!open) {
      setCurrentScene(0);
      setProgress(0);
      setSceneProgress(0);
      setIsPlaying(true);
      return;
    }

    let interval: NodeJS.Timeout;
    
    if (isPlaying) {
      interval = setInterval(() => {
        setSceneProgress((prev) => {
          const newProgress = prev + 50;
          
          if (newProgress >= currentSceneData.duration) {
            if (currentScene < demoScenes.length - 1) {
              setCurrentScene(currentScene + 1);
              return 0;
            } else {
              setIsPlaying(false);
              return currentSceneData.duration;
            }
          }
          return newProgress;
        });
      }, 50);
    }

    return () => clearInterval(interval);
  }, [isPlaying, currentScene, open, currentSceneData.duration]);

  useEffect(() => {
    const elapsed = demoScenes.slice(0, currentScene).reduce((acc, scene) => acc + scene.duration, 0) + sceneProgress;
    setProgress((elapsed / totalDuration) * 100);
  }, [currentScene, sceneProgress, totalDuration]);

  const handleRestart = () => {
    setCurrentScene(0);
    setProgress(0);
    setSceneProgress(0);
    setIsPlaying(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        {/* Video Header */}
        <div className="bg-background border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full" />
            <span className="text-sm font-medium">TravelPal Demo</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onOpenChange(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Video Content */}
        <div className="relative bg-gradient-to-br from-background to-muted/20 h-96 overflow-hidden">
          <div className="absolute inset-0 animate-fade-in">
            {currentSceneData.content}
          </div>
          
          {/* Scene Title Overlay */}
          <div className="absolute top-4 left-4 right-4">
            <div className="bg-black/20 backdrop-blur-sm rounded-full px-3 py-1">
              <span className="text-sm font-medium text-white">{currentSceneData.title}</span>
            </div>
          </div>
        </div>

        {/* Video Controls */}
        <div className="p-4 space-y-3">
          {/* Progress Bar */}
          <div className="space-y-1">
            <Progress value={progress} className="h-1" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{Math.round(progress)}%</span>
              <span>{Math.round((totalDuration - (demoScenes.slice(0, currentScene).reduce((acc, scene) => acc + scene.duration, 0) + sceneProgress)) / 1000)}s left</span>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestart}
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-16"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Scene Indicators */}
          <div className="flex justify-center gap-1">
            {demoScenes.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentScene ? 'bg-primary' : 
                  index < currentScene ? 'bg-primary/50' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}